/**
 * SPARQL Service
 *
 * Handles all SPARQL queries to the knowledge graph for:
 * - RailML files listing (from metadata endpoint)
 * - DIM data queries (from main sparql endpoint)
 */

import config from '@config/env';
import type { RailMLFile, Dataset, DataLineage, DataLineageDataset, SparqlBinding, EntityProperty, EntityObject, KeyFigureSection, KeyFigureTable } from '@/types';

/**
 * Execute SPARQL query against the main DIM data endpoint
 * @param query - SPARQL query string
 * @returns Query results
 */
async function executeSparqlQuery(query: string): Promise<SparqlBinding[]> {
  const endpoint = config.sparqlEndpoint;

  if (!endpoint) {
    throw new Error('SPARQL endpoint not configured. Check VITE_SPARQL_ENDPOINT in .env');
  }

  return executeQuery(endpoint, query);
}

/**
 * Execute SPARQL query against the metadata endpoint
 * @param query - SPARQL query string
 * @returns Query results
 */
async function executeMetadataQuery(query: string): Promise<SparqlBinding[]> {
  const endpoint = config.sparqlMetadataEndpoint;

  if (!endpoint) {
    throw new Error('SPARQL metadata endpoint not configured. Check VITE_SPARQL_METADATA_ENDPOINT in .env');
  }

  return executeQuery(endpoint, query);
}

/**
 * Execute SPARQL query against a given endpoint
 * @param endpoint - SPARQL endpoint URL
 * @param query - SPARQL query string
 * @returns Query results
 */
async function executeQuery(endpoint: string, query: string): Promise<SparqlBinding[]> {

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/sparql-query',
        'Accept': 'application/sparql-results+json'
      },
      credentials: 'include',
      body: query
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`SPARQL query failed: ${response.status} ${response.statusText} - ${error}`);
    }

    const data = await response.json();
    return data.results.bindings || [];
  } catch (error) {
    console.error('[SPARQL Service] Query execution failed:', error);
    throw error;
  }
}

/**
 * Get list of all RailML files with metadata
 * @returns Array of RailML file objects
 */
export async function getRailMLFiles(): Promise<RailMLFile[]> {
  const query = `
PREFIX dcterms: <http://purl.org/dc/terms/>
PREFIX dcat: <http://www.w3.org/ns/dcat#>
PREFIX schema1: <http://schema.org/>
PREFIX spdx: <http://spdx.org/rdf/terms#>

SELECT ?file ?title ?description ?created ?contentUrl
       ?schemaVersion ?filesize ?checksumValue ?subnetworkUri
WHERE {
  ?file a dcat:Distribution;
        dcterms:title ?title;
        dcterms:description ?description;
        dcterms:created ?created;
        schema1:contentUrl ?contentUrl;
        schema1:schemaVersion ?schemaVersion;
        schema1:status ?status;
        dcat:byteSize ?filesize;
        spdx:checksum ?checksum.

  ?checksum spdx:checksumValue ?checksumValue.

  OPTIONAL { ?file dcterms:isPartOf ?subnetworkUri. }

  FILTER(?status = "available")
}
ORDER BY DESC(?created)
  `.trim();

  console.log('[SPARQL Service] Executing getRailMLFiles query against metadata endpoint');

  const results = await executeMetadataQuery(query);

  // Transform SPARQL results to JavaScript objects
  return results.map(row => ({
    uri: row.file.value,
    title: row.title.value,
    description: row.description.value,
    created: new Date(row.created.value),
    contentUrl: row.contentUrl.value,
    schemaVersion: row.schemaVersion.value,
    checksumValue: row.checksumValue.value,
    filesize: row.filesize.value,
    subnetworkUri: row.subnetworkUri?.value ?? null
  }));
}

/**
 * Get data lineage (provenance) for a specific railML file
 * @param fileUri - URI of the railML file
 * @returns Data lineage information with timestamps
 */
export async function getRailMLFileLineage(fileUri: string): Promise<DataLineage | null> {
  const query = `
PREFIX dcat: <http://www.w3.org/ns/dcat#>
PREFIX dcterms: <http://purl.org/dc/terms/>
PREFIX prov: <http://www.w3.org/ns/prov#>
PREFIX schema: <http://schema.org/>

SELECT DISTINCT
    ?railmlTitle
    ?railmlCreated
    ?sourceTtl
    ?sourceTtlName
    ?sourceTtlCreated
    ?kg
    ?kgTitle
    ?kgCreated
    ?kgIdentifier
    ?source
    ?sourceTitle
    ?sourceCreated
    ?sourceIdentifier
WHERE {
    VALUES ?railmlFile { <${fileUri}> }

    # RailML file metadata
    ?railmlFile a dcat:Distribution ;
                dcterms:title ?railmlTitle ;
                dcterms:created ?railmlCreated .

    # Source TTL file
    OPTIONAL {
        ?railmlFile prov:wasDerivedFrom ?sourceTtl .
        ?sourceTtl schema:name ?sourceTtlName .
        OPTIONAL { ?sourceTtl dcterms:created ?sourceTtlCreated }

        # Knowledge Graph that the source TTL was derived from
        OPTIONAL {
            ?sourceTtl prov:wasDerivedFrom ?kg .
            ?kg dcterms:title ?kgTitle ;
                dcterms:created ?kgCreated .
            OPTIONAL { ?kg dcterms:identifier ?kgIdentifier }

            # Sources that the KG was derived from
            OPTIONAL {
                ?kg prov:wasDerivedFrom ?source .
                ?source dcterms:title ?sourceTitle .
                OPTIONAL { ?source dcterms:created ?sourceCreated }
                OPTIONAL { ?source dcterms:identifier ?sourceIdentifier }
            }
        }
    }
}
  `.trim();

  console.log('[SPARQL Service] Executing getRailMLFileLineage query for:', fileUri);

  try {
    const results = await executeMetadataQuery(query);

    if (results.length === 0) {
      return null;
    }

    // Process results - first row has railML and KG info
    const firstRow = results[0];

    // Collect unique sources from all result rows
    const sourcesMap = new Map<string, DataLineageDataset>();
    results.forEach(row => {
      if (row.source && row.sourceTitle) {
        sourcesMap.set(row.source.value, {
          title: row.sourceTitle.value,
          created: row.sourceCreated ? new Date(row.sourceCreated.value) : new Date(),
          identifier: row.sourceIdentifier?.value
        });
      }
    });

    return {
      railmlFile: {
        title: firstRow.railmlTitle.value,
        created: new Date(firstRow.railmlCreated.value)
      },
      sourceTtl: firstRow.sourceTtlName ? {
        title: firstRow.sourceTtlName.value,
        created: firstRow.sourceTtlCreated ? new Date(firstRow.sourceTtlCreated.value) : new Date()
      } : undefined,
      knowledgeGraph: firstRow.kgTitle ? {
        title: firstRow.kgTitle.value,
        created: new Date(firstRow.kgCreated.value),
        identifier: firstRow.kgIdentifier?.value
      } : undefined,
      sources: Array.from(sourcesMap.values())
    };
  } catch (err) {
    console.error('[SPARQL Service] Failed to fetch lineage:', err);
    return null;
  }
}

/**
 * Get list of all DCAT Datasets with latest distribution metadata
 * @returns Array of Dataset objects
 */
export async function getDatasets(language = 'en'): Promise<Dataset[]> {
  const query = `
PREFIX dcat: <http://www.w3.org/ns/dcat#>
PREFIX dcterms: <http://purl.org/dc/terms/>
PREFIX schema1: <http://schema.org/>
PREFIX spdx: <http://spdx.org/rdf/terms#>
PREFIX spin: <http://spinrdf.org/spin#>
PREFIX bno: <https://data.banenor.no/ontology/>
SELECT ?dataset ?titleEn ?titleNb ?titleNn ?descriptionEn ?descriptionNb ?descriptionNn
       ?definitionDate ?distribution ?fileCreated ?contentUrl ?filename ?filesize ?checksumValue ?sparqlQuery
WHERE {
  { SELECT ?dataset (MAX(?c) AS ?fileCreated) WHERE {
      ?dataset a dcat:Dataset ; dcat:distribution ?d . ?d a dcat:Distribution ; dcterms:created ?c .
    } GROUP BY ?dataset }
  ?dataset dcterms:modified ?definitionDate ; dcat:distribution ?distribution .
  ?distribution a dcat:Distribution ; dcterms:created ?fileCreated ;
      schema1:contentUrl ?contentUrl ; schema1:name ?filename ; dcat:byteSize ?filesize ; spdx:checksum ?checksum .
  ?checksum spdx:checksumValue ?checksumValue .
  OPTIONAL { ?dataset dcterms:title ?titleEn . FILTER(LANG(?titleEn) = "en") }
  OPTIONAL { ?dataset dcterms:title ?titleNb . FILTER(LANG(?titleNb) = "nb") }
  OPTIONAL { ?dataset dcterms:title ?titleNn . FILTER(LANG(?titleNn) = "nn") }
  OPTIONAL { ?dataset dcterms:description ?descriptionEn . FILTER(LANG(?descriptionEn) = "en") }
  OPTIONAL { ?dataset dcterms:description ?descriptionNb . FILTER(LANG(?descriptionNb) = "nb") }
  OPTIONAL { ?dataset dcterms:description ?descriptionNn . FILTER(LANG(?descriptionNn) = "nn") }
  OPTIONAL {
    ?dataset bno:usesManagedQuery ?mq .
    { SELECT ?mq (MAX(?vc) AS ?mqCreated) WHERE { ?v dcterms:isVersionOf ?mq ; dcterms:created ?vc } GROUP BY ?mq }
    ?mqVersion dcterms:isVersionOf ?mq ; dcterms:created ?mqCreated ; spin:text ?sparqlQuery .
  }
}
ORDER BY COALESCE(?titleEn, ?titleNb, ?titleNn)
  `.trim();

  console.log('[SPARQL Service] Executing getDatasets query against metadata endpoint');

  const results = await executeMetadataQuery(query);

  const languageCode = language.toLowerCase().split('-')[0];
  const languageOrder = languageCode === 'nn'
    ? ['nn', 'nb', 'en']
    : languageCode === 'nb'
      ? ['nb', 'nn', 'en']
      : ['en', 'nb', 'nn'];

  const getLocalizedValue = (row: SparqlBinding, field: 'title' | 'description'): string => {
    for (const code of languageOrder) {
      const suffix = code.charAt(0).toUpperCase() + code.slice(1);
      const value = row[`${field}${suffix}`]?.value;
      if (value) return value;
    }
    return '';
  };

  return results.map(row => ({
    uri: row.dataset.value,
    title: getLocalizedValue(row, 'title'),
    description: getLocalizedValue(row, 'description'),
    definitionDate: new Date(row.definitionDate.value),
    distribution: row.distribution.value,
    fileCreated: new Date(row.fileCreated.value),
    contentUrl: row.contentUrl.value,
    filename: row.filename.value,
    filesize: row.filesize.value,
    checksumValue: row.checksumValue.value,
    sparqlQuery: row.sparqlQuery?.value,
  }));
}

/**
 * Get all key figure sections with their scalar figures and tabular figures,
 * each with the latest calculated distribution.
 * @param language - UI language code (for title selection)
 * @returns Array of KeyFigureSection objects
 */
export async function getKeyFigureSections(language = 'en'): Promise<KeyFigureSection[]> {
  const scalarsQuery = `
PREFIX bno: <https://data.banenor.no/ontology/>
PREFIX dcat: <http://www.w3.org/ns/dcat#>
PREFIX dcterms: <http://purl.org/dc/terms/>
PREFIX schema1: <http://schema.org/>
PREFIX spin: <http://spinrdf.org/spin#>
SELECT ?section ?sectionTitleEn ?sectionTitleNo ?sectionPosition
       ?figure ?figureTitleEn ?figureTitleNo ?figurePosition ?unitCode ?sparqlQuery
       ?distribution ?created ?value
WHERE {
  ?section a bno:KeyFigureSection ; schema1:position ?sectionPosition .
  OPTIONAL { ?section dcterms:title ?sectionTitleEn . FILTER(LANGMATCHES(LANG(?sectionTitleEn), "en")) }
  OPTIONAL { ?section dcterms:title ?sectionTitleNo . FILTER(LANGMATCHES(LANG(?sectionTitleNo), "nb") || LANGMATCHES(LANG(?sectionTitleNo), "nn") || LANGMATCHES(LANG(?sectionTitleNo), "no")) }
  ?figure a bno:KeyFigure ; dcterms:isPartOf ?section ; schema1:position ?figurePosition ; bno:usesManagedQuery ?figMq .
  { SELECT ?figMq (MAX(?vc) AS ?mqCreated) WHERE { ?v dcterms:isVersionOf ?figMq ; dcterms:created ?vc } GROUP BY ?figMq }
  ?figMqVersion dcterms:isVersionOf ?figMq ; dcterms:created ?mqCreated ; spin:text ?sparqlQuery .
  OPTIONAL { ?figure dcterms:title ?figureTitleEn . FILTER(LANGMATCHES(LANG(?figureTitleEn), "en")) }
  OPTIONAL { ?figure dcterms:title ?figureTitleNo . FILTER(LANGMATCHES(LANG(?figureTitleNo), "nb") || LANGMATCHES(LANG(?figureTitleNo), "nn") || LANGMATCHES(LANG(?figureTitleNo), "no")) }
  OPTIONAL { ?figure schema1:unitCode ?unitCode }
  OPTIONAL {
    { SELECT ?figure (MAX(?c) AS ?created) WHERE { ?figure a bno:KeyFigure ; dcat:distribution ?d . ?d dcterms:created ?c } GROUP BY ?figure }
    ?figure dcat:distribution ?distribution . ?distribution dcterms:created ?created ; schema1:value ?value .
  }
}
ORDER BY ?sectionPosition ?figurePosition
  `.trim();

  const tablesQuery = `
PREFIX bno: <https://data.banenor.no/ontology/>
PREFIX dcat: <http://www.w3.org/ns/dcat#>
PREFIX dcterms: <http://purl.org/dc/terms/>
PREFIX schema1: <http://schema.org/>
PREFIX spin: <http://spinrdf.org/spin#>
SELECT ?section ?sectionTitleEn ?sectionTitleNo ?sectionPosition
       ?table ?tableTitleEn ?tableTitleNo ?tablePosition ?sparqlQuery
       ?distribution ?created ?csvContent
WHERE {
  ?section a bno:KeyFigureSection ; schema1:position ?sectionPosition .
  OPTIONAL { ?section dcterms:title ?sectionTitleEn . FILTER(LANGMATCHES(LANG(?sectionTitleEn), "en")) }
  OPTIONAL { ?section dcterms:title ?sectionTitleNo . FILTER(LANGMATCHES(LANG(?sectionTitleNo), "nb") || LANGMATCHES(LANG(?sectionTitleNo), "nn") || LANGMATCHES(LANG(?sectionTitleNo), "no")) }
  ?table a bno:KeyFigureTable ; dcterms:isPartOf ?section ; schema1:position ?tablePosition ; bno:usesManagedQuery ?tableMq .
  { SELECT ?tableMq (MAX(?vc) AS ?mqCreated) WHERE { ?v dcterms:isVersionOf ?tableMq ; dcterms:created ?vc } GROUP BY ?tableMq }
  ?tableMqVersion dcterms:isVersionOf ?tableMq ; dcterms:created ?mqCreated ; spin:text ?sparqlQuery .
  OPTIONAL { ?table dcterms:title ?tableTitleEn . FILTER(LANGMATCHES(LANG(?tableTitleEn), "en")) }
  OPTIONAL { ?table dcterms:title ?tableTitleNo . FILTER(LANGMATCHES(LANG(?tableTitleNo), "nb") || LANGMATCHES(LANG(?tableTitleNo), "nn") || LANGMATCHES(LANG(?tableTitleNo), "no")) }
  OPTIONAL {
    { SELECT ?table (MAX(?c) AS ?created) WHERE { ?table a bno:KeyFigureTable ; dcat:distribution ?d . ?d dcterms:format "text/csv" ; dcterms:created ?c } GROUP BY ?table }
    ?table dcat:distribution ?distribution . ?distribution dcterms:created ?created ; dcterms:format "text/csv" ; schema1:value ?csvContent .
  }
}
ORDER BY ?sectionPosition ?tablePosition
  `.trim();

  // Columns are fetched separately so the (potentially large) CSV literal is
  // not repeated once per column in the tables result.
  const columnsQuery = `
PREFIX bno: <https://data.banenor.no/ontology/>
PREFIX dcterms: <http://purl.org/dc/terms/>
PREFIX schema1: <http://schema.org/>
PREFIX csvw: <http://www.w3.org/ns/csvw#>
SELECT ?table ?col ?colName ?colTitleEn ?colTitleNo ?colPosition WHERE {
  ?table a bno:KeyFigureTable ; csvw:tableSchema ?schema . ?schema csvw:column ?col .
  ?col csvw:name ?colName ; schema1:position ?colPosition .
  OPTIONAL { ?col dcterms:title ?colTitleEn . FILTER(LANGMATCHES(LANG(?colTitleEn), "en")) }
  OPTIONAL { ?col dcterms:title ?colTitleNo . FILTER(LANGMATCHES(LANG(?colTitleNo), "nb") || LANGMATCHES(LANG(?colTitleNo), "nn") || LANGMATCHES(LANG(?colTitleNo), "no")) }
} ORDER BY ?table ?colPosition
  `.trim();

  console.log('[SPARQL Service] Executing getKeyFigureSections queries against metadata endpoint');

  const [scalarRows, tableRows, columnRows] = await Promise.all([
    executeMetadataQuery(scalarsQuery),
    executeMetadataQuery(tablesQuery),
    executeMetadataQuery(columnsQuery),
  ]);

  const preferNo = language !== 'en';

  const pickTitle = (row: SparqlBinding, enKey: string, noKey: string): string =>
    preferNo
      ? (row[noKey]?.value ?? row[enKey]?.value ?? '')
      : (row[enKey]?.value ?? row[noKey]?.value ?? '');

  const sectionMap = new Map<string, KeyFigureSection>();

  for (const row of scalarRows) {
    const sectionUri = row.section.value;
    if (!sectionMap.has(sectionUri)) {
      sectionMap.set(sectionUri, {
        uri: sectionUri,
        title: pickTitle(row, 'sectionTitleEn', 'sectionTitleNo'),
        position: Number(row.sectionPosition.value),
        scalars: [],
        tables: [],
      });
    }
    const section = sectionMap.get(sectionUri)!;
    const figureUri = row.figure.value;
    if (!section.scalars.find(f => f.uri === figureUri)) {
      section.scalars.push({
        uri: figureUri,
        title: pickTitle(row, 'figureTitleEn', 'figureTitleNo'),
        position: Number(row.figurePosition.value),
        sparqlQuery: row.sparqlQuery.value,
        unitCode: row.unitCode?.value,
        distribution: row.distribution ? {
          created: new Date(row.created.value),
          value: row.value !== undefined ? Number(row.value.value) : undefined,
          isTable: false,
        } : undefined,
      });
    }
  }

  const tableMap = new Map<string, KeyFigureTable>();

  for (const row of tableRows) {
    const sectionUri = row.section.value;
    if (!sectionMap.has(sectionUri)) {
      sectionMap.set(sectionUri, {
        uri: sectionUri,
        title: pickTitle(row, 'sectionTitleEn', 'sectionTitleNo'),
        position: Number(row.sectionPosition.value),
        scalars: [],
        tables: [],
      });
    }
    const tableUri = row.table.value;
    if (!tableMap.has(tableUri)) {
      const kfTable: KeyFigureTable = {
        uri: tableUri,
        title: pickTitle(row, 'tableTitleEn', 'tableTitleNo'),
        position: Number(row.tablePosition.value),
        sparqlQuery: row.sparqlQuery.value,
        columns: [],
        distribution: row.distribution ? {
          created: new Date(row.created.value),
          csvContent: row.csvContent?.value,
          isTable: true,
        } : undefined,
      };
      tableMap.set(tableUri, kfTable);
      sectionMap.get(sectionUri)!.tables.push(kfTable);
    }
  }

  for (const row of columnRows) {
    const kfTable = tableMap.get(row.table.value);
    if (kfTable && !kfTable.columns.find(c => c.name === row.colName.value)) {
      kfTable.columns.push({
        name: row.colName.value,
        titleEn: row.colTitleEn?.value ?? row.colName.value,
        titleNo: row.colTitleNo?.value ?? row.colTitleEn?.value ?? row.colName.value,
        position: Number(row.colPosition.value),
      });
    }
  }

  const sections = Array.from(sectionMap.values()).sort((a, b) => a.position - b.position);
  for (const section of sections) {
    section.scalars.sort((a, b) => a.position - b.position);
    section.tables.sort((a, b) => a.position - b.position);
    for (const table of section.tables) {
      table.columns.sort((a, b) => a.position - b.position);
    }
  }

  return sections;
}

export default {
  query: executeSparqlQuery,
  getRailMLFiles,
  getRailMLFileLineage,
  getDatasets,
  getKeyFigureSections,
  describeEntity
};

/**
 * Describe an entity by URI via the public /describe endpoint.
 * This endpoint is public: signed-in users get every dataset, anonymous
 * visitors only the RINF export and the ontologies.
 * Returns grouped properties with their objects.
 *
 * @param uri - The full URI of the entity to describe
 * @returns Array of EntityProperty objects grouping objects by predicate
 */
export async function describeEntity(uri: string): Promise<EntityProperty[]> {
  const endpoint = config.describeEndpoint;
  // Encode characters that the URL parser would otherwise treat as separators
  // (notably `#`, which the browser strips as a fragment, plus `?`, `&`, `+`
  // which would alter query-string parsing). Other characters in the URI such
  // as `:` and `/` are intentionally left raw so the server-side regex
  // validation that expects a literal `https?://…` keeps matching for the
  // common case of internal data/ontology URIs.
  const safeUri = uri.replace(/[#?&+]/g, encodeURIComponent);
  const url = `${endpoint}?uri=${safeUri}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/sparql-results+json',
      },
      // The sign-in cookie decides the answer: everything when signed in, only
      // the RINF export and the ontologies for anonymous visitors
      credentials: 'same-origin',
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Describe query failed: ${response.status} ${response.statusText} - ${error}`);
    }

    const data = await response.json();
    const bindings: SparqlBinding[] = data.results?.bindings || [];

    // Group objects by predicate
    const propertyMap = new Map<string, EntityObject[]>();

    for (const binding of bindings) {
      const predicate = binding.predicate?.value;
      const obj = binding.object;

      if (!predicate || !obj) continue;

      const entityObj: EntityObject = {
        value: obj.value,
        type: obj.type === 'uri' ? 'uri' : obj.type === 'bnode' ? 'bnode' : 'literal',
        datatype: obj.datatype,
        lang: obj['xml:lang'],
      };

      const existing = propertyMap.get(predicate);
      if (existing) {
        existing.push(entityObj);
      } else {
        propertyMap.set(predicate, [entityObj]);
      }
    }

    return Array.from(propertyMap.entries()).map(([predicate, objects]) => ({
      predicate,
      objects,
    }));
  } catch (error) {
    console.error('[SPARQL Service] Describe query failed:', error);
    throw error;
  }
}
